import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import type { WholesaleBreak } from '../../api/superAdmin';

type BreakRow = { min_qty: string; price: string };

type Props = {
  /** Source schedule; used to seed the editor and re-seed when hydrateKey changes. */
  value: WholesaleBreak[];
  onChange: (breaks: WholesaleBreak[]) => void;
  /** Change this (e.g. product/variant id, or a load counter) to re-seed from value. */
  hydrateKey?: string | number;
  disabled?: boolean;
  compact?: boolean;
};

const toRows = (value: WholesaleBreak[]): BreakRow[] => {
  if (!value || value.length === 0) return [{ min_qty: '', price: '' }];
  return [...value]
    .sort((a, b) => Number(a.min_qty) - Number(b.min_qty))
    .map((b) => ({ min_qty: String(b.min_qty ?? ''), price: String(b.price ?? '') }));
};

const parseRows = (rows: BreakRow[]): WholesaleBreak[] =>
  rows
    .map((r) => ({
      min_qty: parseInt(r.min_qty, 10) || 0,
      price: parseFloat(r.price) || 0,
    }))
    .filter((b) => b.min_qty > 0 && b.price > 0);

/**
 * Edits a wholesale quantity-break schedule (min qty → unit price). The smallest
 * quantity acts as the MOQ. Emits the cleaned schedule on every change.
 */
const WholesaleBreaksEditor: React.FC<Props> = ({
  value,
  onChange,
  hydrateKey,
  disabled = false,
  compact = false,
}) => {
  const [rows, setRows] = useState<BreakRow[]>(() => toRows(value));

  // Re-seed when the parent hydrates a different product/variant.
  useEffect(() => {
    setRows(toRows(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrateKey]);

  const commit = (next: BreakRow[]) => {
    setRows(next);
    onChange(parseRows(next));
  };

  const updateRow = (i: number, key: keyof BreakRow, val: string) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r));
    commit(next);
  };

  const addRow = () => commit([...rows, { min_qty: '', price: '' }]);

  const removeRow = (i: number) => {
    const next = rows.filter((_, idx) => idx !== i);
    commit(next.length ? next : [{ min_qty: '', price: '' }]);
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Wholesale price by quantity. Leave blank for no wholesale price. The smallest
        quantity is the minimum order.
      </Typography>

      {rows.map((row, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          <TextField
            label={i === 0 ? 'Min qty' : undefined}
            type="number"
            size="small"
            value={row.min_qty}
            disabled={disabled}
            onChange={(e) => updateRow(i, 'min_qty', e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            sx={{ width: compact ? 90 : 120 }}
            placeholder="Qty"
          />
          <Typography color="text.secondary">→</Typography>
          <TextField
            label={i === 0 ? 'Unit price' : undefined}
            type="number"
            size="small"
            value={row.price}
            disabled={disabled}
            onChange={(e) => updateRow(i, 'price', e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ width: compact ? 110 : 150 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            placeholder="Price"
          />
          <Tooltip title="Remove break">
            <span>
              <IconButton
                size="small"
                onClick={() => removeRow(i)}
                disabled={disabled}
                aria-label="Remove break"
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ))}

      <Button
        size="small"
        startIcon={<Add />}
        onClick={addRow}
        disabled={disabled}
        sx={{ mt: 0.5, textTransform: 'none' }}
      >
        Add quantity break
      </Button>
    </Box>
  );
};

export default WholesaleBreaksEditor;
